import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, waLink, WA_MESSAGES } from "../../data";

const Navbar = ({ onNavigate }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    onNavigate(href);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        data-testid="landing-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
          scrolled
            ? "border-b border-white/10 bg-[#0F1115]/95 shadow-[0_10px_40px_rgba(0,0,0,0.35)] backdrop-blur-sm"
            : "border-b border-white/10 bg-black/35 backdrop-blur-sm"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:h-20 lg:px-8">
          <a href="#beranda" onClick={(e) => go(e, "#beranda")} aria-label="Beranda">
            <Logo dark />
          </a>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Navigasi utama">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={l.testid}
                className="font-tech text-[11px] uppercase tracking-[0.18em] text-white/70 transition-colors duration-300 hover:text-[#DF9F00]"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={waLink(WA_MESSAGES.general)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-button"
              className="hidden items-center gap-2 rounded-lg bg-[#DF9F00] px-5 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-[#141619] transition-colors duration-300 hover:bg-white sm:flex"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={2.5} />
              WhatsApp
            </a>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-button"
              aria-label="Buka menu"
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/20 text-white lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-menu-drawer"
            className="fixed inset-0 z-[60] flex flex-col bg-[#0F1115]"
          >
            <div className="flex h-16 items-center justify-between px-5">
              <Logo dark />
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Tutup menu"
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/20 text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-6">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  data-testid={`drawer-${l.testid}`}
                  initial={{ x: -32, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="font-display border-b border-white/10 py-4 text-3xl font-semibold uppercase tracking-wide text-white transition-colors hover:text-[#DF9F00]"
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
            <div className="p-6">
              <a
                href={waLink(WA_MESSAGES.general)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="drawer-whatsapp-button"
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#DF9F00] py-4 text-sm font-bold uppercase tracking-[0.12em] text-[#141619]"
              >
                <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
                Chat WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
